import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Calendar, Package, MessageCircle } from "lucide-react";
import api, { formatApiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import { TripCard } from "@/components/Cards";
import { UserIdentity } from "@/components/UserIdentity";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function ParcelDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const [parcel, setParcel] = useState(null);
  const [showOrig, setShowOrig] = useState(false);
  const [matches, setMatches] = useState(null);

  useEffect(() => {
    api.get(`/parcels/${id}`, { params: { lang } }).then((r) => setParcel(r.data)).catch(() => setParcel(false));
    api.get(`/parcels/${id}/matches`, { params: { with_stats: 1, lang } }).then((r) => setMatches(r.data)).catch(() => setMatches({ matches: [], route_only: 0 }));
  }, [id, lang]);

  if (parcel === null) return <div className="max-w-7xl mx-auto px-6 py-16 text-slate-500">{t("common.loading")}</div>;
  if (parcel === false) return <div className="max-w-7xl mx-auto px-6 py-16 text-slate-500">{t("common.error")}</div>;

  const sizeLabel = { s: t("parcel.sizeS"), m: t("parcel.sizeM"), l: t("parcel.sizeL") }[parcel.size];
  const isOwn = user && user.id === parcel.sender_id;

  const contactSender = async () => {
    if (!user) {
      toast.info(t("auth.loginRequired"));
      navigate(`/entrar?next=/encomenda/${id}`);
      return;
    }
    try {
      const { data } = await api.post("/conversations", { user_id: parcel.sender_id });
      navigate(`/mensagens/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="parcel-detail-page">
      <div className="bg-white rounded-2xl border border-slate-100 p-6 md:p-10 shadow-sm mb-10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-primary mb-3">{parcel.origin} → {parcel.destination}</h1>
            <p className="text-slate-600 mb-5 max-w-2xl">{showOrig ? parcel.description : (parcel._tr?.description || parcel.description)}</p>
            {parcel._tr?.description && (
              <button type="button" data-testid="parcel-original-toggle" onClick={() => setShowOrig(!showOrig)} className="block text-xs text-accent font-semibold mb-5 -mt-3 underline underline-offset-2">{showOrig ? t("translate.showTranslation") : t("translate.showOriginal")}</button>
            )}
            <div className="flex flex-wrap gap-4 text-sm text-slate-500">
              <span className="flex items-center gap-1.5"><Package size={15} className="text-accent" />{sizeLabel} · {parcel.weight}kg</span>
              {parcel.departure_date && <span className="flex items-center gap-1.5" data-testid="parcel-departure-date"><Calendar size={15} className="text-accent" />{t("parcel.departureDate")}: {parcel.departure_date}</span>}
              <span className="flex items-center gap-1.5"><Calendar size={15} className="text-accent" />{t("parcel.deliverBy")} {parcel.date}</span>
            </div>
            {parcel.photos?.length > 0 && (
              <div className="flex flex-wrap gap-3 mt-5" data-testid="parcel-photos-gallery">
                {parcel.photos.map((p, i) => (
                  <a key={p} href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} target="_blank" rel="noreferrer" data-testid={`parcel-photo-${i}`}>
                    <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${p}`} alt="" className="w-24 h-24 object-cover rounded-xl border border-slate-200 hover:opacity-90 transition-opacity" />
                  </a>
                ))}
              </div>
            )}
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-accent">{parcel.price_offer > 0 ? `${parcel.price_offer}€` : t("trip.toArrange")}</p>
            <p className="text-xs text-slate-400 mt-1">{t("parcel.sender")}</p>
            <div className="mt-1.5 flex justify-end"><UserIdentity user={parcel.sender} testid="sender-profile-link" /></div>
            {!isOwn && (
              <Button data-testid="contact-sender-button" onClick={contactSender} variant="outline" className="rounded-full mt-4">
                <MessageCircle size={15} className="mr-2" />{t("parcel.contactSender")}
              </Button>
            )}
          </div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-primary mb-6" data-testid="matches-heading">{t("parcel.matches")}</h2>
      {matches === null ? (
        <p className="text-slate-500">{t("common.loading")}</p>
      ) : matches.matches.length === 0 ? (
        <div className="bg-white border border-slate-100 rounded-2xl p-10 text-center text-slate-500" data-testid="matches-empty">
          {t("parcel.noMatches")}
          {matches.route_only > 0 && <p className="text-xs text-amber-600 mt-3" data-testid="matches-route-only-hint">{t("parcel.routeOnlyHint")}</p>}
        </div>
      ) : (
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-testid="matches-list">
          {matches.matches.map((trip) => <TripCard key={trip.id} trip={trip} />)}
        </div>
      )}
    </div>
  );
}
